import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { criarMola, elastico, maisPerto, prefereMovimentoReduzido, projetar } from '../design/mola'

// JANELA ARRASTAVEL (sheet): sobe de baixo com mola, segue o dedo/mouse 1:1 a partir de onde foi agarrada, continua com o impulso da soltura ate a
// parada mais perto da projecao (topo, meio ou fechada), resiste como elastico acima do topo, pode ser agarrada no meio do movimento e sai pelo
// mesmo caminho por onde entrou. Com "reduzir movimento": sem mola, so um fade.
interface Props { aberta: boolean; aoFechar: () => void; rotulo: string; children: React.ReactNode }

export default function FolhaArrastavel({ aberta, aoFechar, rotulo, children }: Props) {
  const [montada, setMontada] = useState(false)
  const folha = useRef<HTMLElement>(null), cortina = useRef<HTMLDivElement>(null)
  const mola = useRef(criarMola()).current
  const geo = useRef({ alt: 520, cheio: 0, meio: 0, fechado: 0 })
  const visivel = useRef(false), foco = useRef<HTMLElement | null>(null), arraste = useRef<{ y0: number; pos0: number; hist: { t: number; y: number }[] } | null>(null)

  const calcular = () => { const H = window.innerHeight, alt = Math.min(600, Math.round(H * 0.86)); geo.current = { alt, cheio: H - alt, meio: H - Math.round(alt * 0.68), fechado: H + 24 }; if (folha.current) folha.current.style.height = `${alt}px` }
  const pintar = useCallback((y: number) => {
    const f = folha.current, c = cortina.current; if (!f || !c) return
    f.style.transform = `translateY(${y}px)`
    const H = window.innerHeight, p = Math.max(0, Math.min(1, (H - y - 60) / (geo.current.alt - 60)))
    c.style.opacity = String(p * 0.9); c.style.pointerEvents = p > 0.05 ? 'auto' : 'none'
  }, [])
  const esconder = useCallback(() => { visivel.current = false; setMontada(false); const f = foco.current; foco.current = null; if (f && f.focus) f.focus() }, [])

  useEffect(() => {
    mola.aoMover(pintar); mola.aoFim(() => { if (mola.x >= geo.current.fechado - 1) esconder() })
    return () => mola.para()
  }, [mola, pintar, esconder])

  const ir = useCallback((alvo: number, o?: { velocidade?: number; damping?: number; response?: number }) => {
    if (prefereMovimentoReduzido()) { mola.para(); mola.set(alvo); return }          // sem mola: vai direto (o fade fica por conta do abrir/fechar)
    mola.vai(alvo, { damping: 1, response: 0.4, ...o })
  }, [mola])

  useLayoutEffect(() => {
    calcular()
    if (aberta) {
      if (!visivel.current) {
        visivel.current = true; foco.current = document.activeElement as HTMLElement; setMontada(true); mola.para(); mola.set(geo.current.fechado)
        if (prefereMovimentoReduzido() && folha.current) { folha.current.style.opacity = '0'; requestAnimationFrame(() => { if (folha.current) folha.current.style.opacity = '1' }) }
      }
      if (folha.current && !prefereMovimentoReduzido()) folha.current.style.opacity = '1'
      ir(geo.current.meio)
      requestAnimationFrame(() => { folha.current && folha.current.focus({ preventScroll: true } as any) })
    } else if (visivel.current) {
      if (prefereMovimentoReduzido()) { if (folha.current) folha.current.style.opacity = '0'; ir(geo.current.fechado); setTimeout(esconder, 200) }
      else ir(geo.current.fechado, { response: 0.38 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta])

  const baixar = (e: React.PointerEvent) => {
    const alvo = e.target as HTMLElement
    if (alvo.closest('button,a,input,select,textarea,[data-sem-arraste]')) return
    ;(e.currentTarget as HTMLElement).setPointerCapture && (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    mola.para()                                                       // agarrou no meio do movimento: para e parte da posicao ao vivo
    arraste.current = { y0: e.clientY, pos0: mola.x, hist: [{ t: performance.now(), y: mola.x }] }
  }
  const mexer = (e: React.PointerEvent) => {
    const a = arraste.current; if (!a) return
    const { cheio, alt } = geo.current
    let y = a.pos0 + (e.clientY - a.y0)                                // 1:1 com o dedo, respeitando onde agarrou
    if (y < cheio) y = cheio - elastico(cheio - y, alt)                // elastico acima do topo
    mola.set(y); a.hist.push({ t: performance.now(), y }); if (a.hist.length > 6) a.hist.shift()
  }
  const soltar = () => {
    const a = arraste.current; if (!a) return; arraste.current = null
    const p = a.hist[0], q = a.hist[a.hist.length - 1], vel = ((q.y - p.y) / Math.max(16, q.t - p.t)) * 1000
    const { cheio, meio, fechado } = geo.current
    const alvo = maisPerto([cheio, meio, fechado], mola.x + projetar(vel))
    if (alvo === fechado) aoFechar()
    ir(alvo, { velocidade: vel, damping: Math.abs(vel) > 300 ? 0.8 : 1, response: 0.35 })
  }

  return (
    <>
      <div ref={cortina} data-testid="cortina" onClick={aoFechar} style={{ position: 'fixed', inset: 0, background: 'var(--color-overlay)', opacity: 0, pointerEvents: 'none', zIndex: 2000 }} />
      <section ref={folha} hidden={!montada} role="dialog" aria-modal="true" aria-label={rotulo} tabIndex={-1} className="vidro-folha"
        onPointerDown={baixar} onPointerMove={mexer} onPointerUp={soltar} onPointerCancel={soltar} onKeyDown={(e) => { if (e.key === 'Escape') aoFechar() }}
        style={{ position: 'fixed', left: '50%', top: 0, width: 'min(640px, 100%)', height: geo.current.alt, marginLeft: 'calc(min(640px, 100%) / -2)', zIndex: 2001, touchAction: 'none', willChange: 'transform', display: montada ? 'flex' : 'none', flexDirection: 'column' }}>
        <div style={{ width: '2.5rem', height: '0.3rem', borderRadius: '1rem', background: 'var(--color-border-strong)', margin: '0.6rem auto 0.8rem', cursor: 'grab', flex: 'none' }} aria-hidden="true" />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '0 1.25rem 1.25rem' }}>{montada ? children : null}</div>
      </section>
    </>
  )
}
